// ── Le pont du copilote : entre l'onglet Assistant et l'editeur (chantier COP) ──
// L'editeur s'y inscrit tant qu'il est monte ; l'onglet lui demande le plan, lui fait montrer une proposition et
// l'appliquer. Rien ne touche au projet avant « Appliquer » (C4), et une proposition dont le plan a change ne s'applique
// plus (C9).
import type { CopiloteAction } from '@/lib/schemas/copilote.schema';
import type { Point, ProjectState } from '@/lib/types';
import { preparer, type EtapesOk, type Etape, type Preparation } from './actions';
import { murLePlusProche, pxToCm, resumerPlan } from './resume';

export interface EditeurCopilote {
  projet(): ProjectState;
  etageId(): string;
  /** Pourquoi l'editeur ne peut pas agir : projet en lecture seule, vue 3D ; null s'il le peut */
  raison(): 'lecture' | '3d' | null;
  /** Dessine l'apercu dans le plan, ou l'efface */
  montrer(apercu: EtapesOk['apercu'] | null, titre?: string): void;
  /** Joue les etapes en un seul pas d'historique */
  jouer(etapes: Etape[], label: string): void | Promise<void>;
  /** Le plan passe en visee : le point clique en pixels du plan, ou null sur Echap (C7) */
  viser(): Promise<Point | null>;
  /** Revient a la vue 2D (P06) */
  passer2D(): void;
}

export type EtatProposition = 'appliquee' | 'ecartee' | 'annulee' | 'perimee';
export type ContexteCopilote = { plan: string } | { raison: 'accueil' | 'lecture' | '3d' };

let editeur: EditeurCopilote | null = null;
let ids: Record<string, string> = {};
/** La proposition en cours, et le projet et l'etage tels qu'ils etaient quand elle a ete faite */
let courante: { prep: EtapesOk; avant: ProjectState; etage: string; appliquee: boolean } | null = null;
const abonnes = new Set<(etat: EtatProposition) => void>();
const annoncer = (etat: EtatProposition) => abonnes.forEach(f => f(etat));

export function inscrireEditeur(e: EditeurCopilote): () => void {
  editeur = e;
  return () => {
    if (editeur === e) {
      editeur = null;
      // L'editeur s'en va : la carte de la proposition ne doit plus offrir « Appliquer »
      if (courante && !courante.appliquee) annoncer('ecartee');
      courante = null;
    }
  };
}

export function abonnerCopilote(f: (etat: EtatProposition) => void): () => void {
  abonnes.add(f);
  return () => { abonnes.delete(f); };
}

/** Ce que la question emporte : le resume du plan, ou la raison pour laquelle l'assistant ne peut pas agir (C1) */
export function contexteCopilote(): ContexteCopilote {
  if (!editeur) return { raison: 'accueil' };
  const raison = editeur.raison();
  if (raison) return { raison };
  const resume = resumerPlan(editeur.projet(), editeur.etageId());
  ids = resume.ids;
  return { plan: resume.texte };
}

/** Verifie la proposition du modele et, acceptee, la dessine dans le plan */
export function proposer(actions: CopiloteAction[]): Preparation {
  if (!editeur) return { ok: false, raison: 'L\'éditeur n\'est plus ouvert.' };
  const projet = editeur.projet();
  const prep = preparer(projet, editeur.etageId(), actions, ids);
  if (prep.ok) {
    courante = { prep, avant: projet, etage: editeur.etageId(), appliquee: false };
    editeur.montrer(prep.apercu, prep.titre);
  }
  return prep;
}

/** Applique la proposition en attente ; false si elle n'existe plus, ou si le plan a change */
export function appliquer(): boolean {
  if (!editeur || !courante || courante.appliquee || editeur.projet() !== courante.avant) return false;
  if (editeur.etageId() !== courante.etage || editeur.raison()) return false;
  courante.appliquee = true;
  editeur.montrer(null);
  editeur.jouer(courante.prep.etapes, `Assistant : ${courante.prep.titre}`);
  annoncer('appliquee');
  return true;
}

export function ecarter(): void {
  if (!courante) return;
  const attente = !courante.appliquee;
  courante = null;
  editeur?.montrer(null);
  if (attente) annoncer('ecartee');
}

/** L'editeur signale chaque changement du projet : proposition perimee (C9), ou appliquee puis annulee (C6) */
export function projetChange(projet: ProjectState): void {
  if (!courante) return;
  if (courante.appliquee) {
    if (projet === courante.avant) {
      courante = null;
      annoncer('annulee');
    }
    return;
  }
  if (projet !== courante.avant) {
    courante = null;
    editeur?.montrer(null);
    annoncer('perimee');
  }
}

export const passer2D = () => editeur?.passer2D();

/** L'editeur signale un changement d'etage ou de vue : une proposition en attente ne vaut plus (C9) */
export function editeurChange(): void {
  if (!editeur || !courante || courante.appliquee) return;
  if (editeur.etageId() !== courante.etage || editeur.raison()) {
    courante = null;
    editeur.montrer(null);
    annoncer('perimee');
  }
}

/** La visee : l'endroit montre sur le plan, en centimetres ; null si elle est annulee (C7) */
export async function viser(): Promise<(Point & { mur?: string; position?: number }) | null> {
  if (!editeur) return null;
  const point = await editeur.viser();
  if (!point) return null;
  const projet = editeur.projet();
  const cm = { x: pxToCm(point.x, projet.calibration), y: pxToCm(point.y, projet.calibration) };
  return { ...cm, ...murLePlusProche(projet, editeur.etageId(), cm) };
}
